import { ContextFlag, FeatureType, type GaContextAsp, type GaContextCustomer, type GaContextDataSync, Language, type RoleResource } from "@newgenerated/shared/schema";
import { getTranslationFunction, type TranslationFn } from "@utils/vue-migration/translationUtils";
import { createHasAccessFunction, setupRoleResourceConstraints } from "@utils/roleService";
import { assert } from "@utils/assertion";
import { getContextFlags } from "@newgenerated/shared/stubs/getContextFlags";
import { getFeatures } from "@newgenerated/shared/stubs/getFeatures";

export type HasAccessFn = (resource: RoleResource, language?: Language, corporateId?: number) => boolean;
export type GetTranslationFunctionFn = (lang: Language) => TranslationFn;
export type FeatureContext = Record<FeatureType, boolean>;
export type FlagContext = Record<ContextFlag, boolean>;
export type CustomerContext = GaContextCustomer | null;
export type AspContext = GaContextAsp | null;

type ContextEntry = [key: string, value: unknown];

export async function createContext(options: string[], currentLanguage: Language, data: GaContextDataSync): Promise<Record<string, unknown>> {
  async function setupHasAccessFn(): Promise<ContextEntry> {
    const constraints = await setupRoleResourceConstraints();
    return ["hasAccess", createHasAccessFunction(constraints)];
  }

  async function setupFeatures(): Promise<ContextEntry> {
    const features = await getFeatures();
    return [
      "features",
      FeatureType.getValues().reduce((acc, curr) => {
        return { ...acc, [curr]: features.kind === "success" && features.data.items.includes(curr) };
      }, {} as FeatureContext),
    ];
  }

  async function setupAllTranslationFn(): Promise<ContextEntry> {
    const translationFns = new Map(
      await Promise.all(
        Language.getValues().map(async (lang): Promise<[Language, TranslationFn]> => {
          return [lang, await getTranslationFunction(lang)];
        }),
      ),
    );

    return [
      "getTranslationFunction",
      (lang: Language): TranslationFn => {
        const translationFn = translationFns.get(lang);
        assert(translationFn !== undefined, `Translation function for language '${lang}' not found`);
        return translationFn;
      },
    ];
  }

  async function setupTranslationFn(lang: Language): Promise<ContextEntry> {
    const t = await getTranslationFunction(lang);
    return ["t", t];
  }

  async function setupContextFlags(): Promise<ContextEntry> {
    const flags = await getContextFlags();
    return [
      "flags",
      ContextFlag.getValues().reduce((acc, curr) => {
        return { ...acc, [curr]: flags.kind === "success" && flags.data.items.includes(curr) };
      }, {} as FlagContext),
    ];
  }

  const promises: Promise<ContextEntry>[] = [];
  if (options.includes("HAS_ACCESS")) {
    promises.push(setupHasAccessFn());
  }

  if (options.includes("FEATURES")) {
    promises.push(setupFeatures());
  }

  if (options.includes("ALL_LANGUAGES")) {
    promises.push(setupAllTranslationFn());
  }

  if (options.includes("FLAGS")) {
    promises.push(setupContextFlags());
  }

  promises.push(setupTranslationFn(currentLanguage));

  const contextEntries = await Promise.all(promises);

  contextEntries.push(["currentLanguage", currentLanguage]);
  contextEntries.push(["customer", data.customer]);
  contextEntries.push(["asp", data.asp]);

  return Object.fromEntries(new Map(contextEntries).entries());
}
